import { t } from "@lingui/macro";
import { DEFAULT_WHATSAPP_SETTINGS } from "@repo/common";
import { functions, httpsCallable } from "@repo/ui";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useData } from "../contexts/DataContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export function WhatsAppAdvancedSettings() {
	const { user, companyProfile } = useData();
	const [open, setOpen] = useState(false),
		[busy, setBusy] = useState(false),
		[feedback, setFeedback] = useState<"saved" | "error" | null>(null);
	const defaults =
		companyProfile?.whatsappSettings || DEFAULT_WHATSAPP_SETTINGS;
	const [inactivity, setInactivity] = useState(
		String(defaults.conversationInactivitySeconds / 3600),
	);
	const [human, setHuman] = useState(
		String(defaults.humanTimeoutSeconds / 3600),
	);
	const [grace, setGrace] = useState(String(defaults.responseGraceSeconds));
	useEffect(() => {
		if (!user?.companyId || user.role !== "admin") {
			setOpen(false);
			return;
		}
		setOpen(false);
		setFeedback(null);
	}, [user?.companyId, user?.role]);
	useEffect(() => {
		setInactivity(String(defaults.conversationInactivitySeconds / 3600));
		setHuman(String(defaults.humanTimeoutSeconds / 3600));
		setGrace(String(defaults.responseGraceSeconds));
	}, [
		defaults.conversationInactivitySeconds,
		defaults.humanTimeoutSeconds,
		defaults.responseGraceSeconds,
	]);
	if (user?.role !== "admin" || !user.companyId) return null;
	const seconds = (s: string) => Math.round(Number(s) * 3600);
	const valid =
		[inactivity, human].every(
			(s) =>
				s.trim() &&
				Number.isFinite(Number(s)) &&
				seconds(s) >= 1 &&
				seconds(s) <= 2592000,
		) &&
		grace.trim() &&
		Number.isInteger(Number(grace)) &&
		Number(grace) >= 0 &&
		Number(grace) <= 30;
	async function save() {
		if (!valid) return;
		setBusy(true);
		setFeedback(null);
		try {
			await httpsCallable(
				functions,
				"saveWhatsAppSettings",
			)({
				companyId: user!.companyId,
				settings: {
					conversationInactivitySeconds: seconds(inactivity),
					humanTimeoutSeconds: seconds(human),
					responseGraceSeconds: Number(grace),
				},
			});
			setFeedback("saved");
		} catch {
			setFeedback("error");
		} finally {
			setBusy(false);
		}
	}
	return (
		<section className="mt-6 border-t pt-4">
			<Button
				variant="ghost"
				className="w-full justify-between px-0"
				aria-expanded={open}
				aria-controls="whatsapp-advanced-settings"
				onClick={() => setOpen((v) => !v)}
			>
				{t({ id: "whatsappAdvanced.title", message: "Advanced settings" })}
				<ChevronDown
					className={`size-4 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</Button>
			{open && (
				<form
					id="whatsapp-advanced-settings"
					className="mt-4 space-y-5"
					onSubmit={(e) => {
						e.preventDefault();
						void save();
					}}
				>
					<p className="text-sm text-muted-foreground">
						{t({
							id: "whatsappAdvanced.description",
							message:
								"Apply company defaults to WhatsApp conversations. A customer-specific timeout set with /timeout takes precedence.",
						})}
					</p>
					<div className="grid gap-5 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="wa-inactivity">
								{t({
									id: "whatsappAdvanced.inactivity",
									message: "End conversation after inactivity (hours)",
								})}
							</Label>
							<Input
								id="wa-inactivity"
								type="number"
								min="0.000278"
								max="720"
								step="any"
								value={inactivity}
								onChange={(e) => {
									setInactivity(e.target.value);
									setFeedback(null);
								}}
							/>
							<p className="text-xs text-muted-foreground">
								{t({
									id: "whatsappAdvanced.inactivityHelp",
									message:
										"The next message starts a new conversation. Default: 24 hours.",
								})}
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="wa-human">
								{t({
									id: "whatsappAdvanced.human",
									message: "Human reply pauses AI for (hours)",
								})}
							</Label>
							<Input
								id="wa-human"
								type="number"
								min="0.000278"
								max="720"
								step="any"
								value={human}
								onChange={(e) => {
									setHuman(e.target.value);
									setFeedback(null);
								}}
							/>
							<p className="text-xs text-muted-foreground">
								{t({
									id: "whatsappAdvanced.humanHelp",
									message:
										"Measured from the last human reply. Customer messages do not extend the pause. Default: 12 hours.",
								})}
							</p>
						</div>
					</div>
					<div className="max-w-xs space-y-2">
						<Label htmlFor="wa-grace">
							{t({
								id: "whatsappAdvanced.grace",
								message: "Wait before AI responds (seconds)",
							})}
						</Label>
						<Input
							id="wa-grace"
							type="number"
							min="0"
							max="30"
							step="1"
							value={grace}
							onChange={(e) => {
								setGrace(e.target.value);
								setFeedback(null);
							}}
						/>
					</div>
					<div className="flex flex-wrap items-center gap-3">
						<Button type="submit" disabled={busy || !valid}>
							{busy
								? t({ id: "whatsappAdvanced.saving", message: "Saving…" })
								: t({
										id: "whatsappAdvanced.save",
										message: "Save WhatsApp settings",
									})}
						</Button>
						{feedback && (
							<p
								role={feedback === "error" ? "alert" : "status"}
								className="text-sm"
							>
								{feedback === "saved"
									? t({
											id: "whatsappAdvanced.saved",
											message: "WhatsApp settings saved.",
										})
									: t({
											id: "whatsappAdvanced.error",
											message:
												"Could not save settings. Check your values and try again.",
										})}
							</p>
						)}
					</div>
				</form>
			)}
		</section>
	);
}
